/**
 * The arithmetic behind a pane a person can drag wider or narrower.
 *
 * Plain functions, because this workspace has no DOM test environment and every
 * rule that lives inside a pointer handler is a rule nobody can cover. The
 * handle (`ResizeHandle.tsx`) and the hook (`usePaneWidth.ts`) only carry these
 * out.
 *
 * DIRECTION is which way the pane grows as the handle moves right. A pane on
 * the left of its handle (the inbox's label rail) grows as the handle moves
 * right, so its direction is 1. A pane on the right of its handle (the thread's
 * details column) shrinks as the handle moves right, so its direction is -1.
 * The arrow keys move the HANDLE, the way a splitter is expected to behave, and
 * the direction turns that into a width.
 */

export interface PaneBounds {
  min: number
  max: number
  /** The width a pane opens at, and what Home and a double click go back to. */
  initial: number
}

export type PaneDirection = 1 | -1

/** How far one arrow key press moves the handle, in CSS pixels. */
export const PANE_KEY_STEP = 16

/** A width inside the bounds, rounded to a whole pixel. Anything that is not a number reads as the default. */
export function clampPaneWidth(width: number, bounds: PaneBounds): number {
  if (!Number.isFinite(width)) return bounds.initial
  return Math.round(Math.min(bounds.max, Math.max(bounds.min, width)))
}

/** The width while dragging: where the pane started, plus how far the pointer has travelled. */
export function dragPaneWidth(
  startWidth: number,
  startX: number,
  currentX: number,
  direction: PaneDirection,
  bounds: PaneBounds,
): number {
  return clampPaneWidth(startWidth + (currentX - startX) * direction, bounds)
}

/**
 * The width after a key press, or null for a key the handle leaves alone (so
 * Tab, Enter and the rest still do what they do everywhere else).
 *
 * Left and Right move the handle one step. Home puts the pane back at its
 * default, which is the one key a person reaches for after dragging something
 * into a shape they did not want. End goes to the widest the pane allows.
 */
export function keyPaneWidth(
  key: string,
  width: number,
  direction: PaneDirection,
  bounds: PaneBounds,
  step: number = PANE_KEY_STEP,
): number | null {
  switch (key) {
    case 'ArrowLeft':
      return clampPaneWidth(width - step * direction, bounds)
    case 'ArrowRight':
      return clampPaneWidth(width + step * direction, bounds)
    case 'Home':
      return bounds.initial
    case 'End':
      return bounds.max
    default:
      return null
  }
}

/**
 * Where a width is kept for one person on one machine.
 *
 * Per person because two people sharing a laptop can want different columns,
 * and a width one of them dragged should not greet the other. A browser with
 * nobody signed in yet keeps it under `anyone`, which is never read once a
 * session exists.
 */
export function paneStorageKey(pane: string, personId: string | null | undefined): string {
  return `gjb-pane:${pane}:${personId === null || personId === undefined || personId === '' ? 'anyone' : personId}`
}

/**
 * The stored width, or the default.
 *
 * Wrapped because storage throws rather than returning null in a browser set to
 * block site data, and a pane that will not render because of that is worse
 * than a pane at its default width. A stored value outside today's bounds is
 * clamped rather than trusted, so tightening a bound in a later release cannot
 * leave somebody with a pane wider than the new maximum.
 */
export function readPaneWidth(
  storage: Pick<Storage, 'getItem'> | undefined,
  key: string,
  bounds: PaneBounds,
): number {
  try {
    const raw = storage?.getItem(key)
    if (raw === null || raw === undefined || raw.trim() === '') return bounds.initial
    return clampPaneWidth(Number(raw), bounds)
  } catch {
    return bounds.initial
  }
}

/**
 * Whether a pane a person can open and close was left open. Off when nothing
 * usable is stored, and never throws, for the same reasons as the width.
 */
export function readPaneOpen(storage: Pick<Storage, 'getItem'> | undefined, key: string): boolean {
  try {
    return storage?.getItem(key) === 'open'
  } catch {
    return false
  }
}

/** Remembers open, and forgets closed, which is the default. Never throws. */
export function writePaneOpen(storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined, key: string, open: boolean): void {
  try {
    if (open) storage?.setItem(key, 'open')
    else storage?.removeItem(key)
  } catch {
    // Blocked storage: the choice lasts as long as the page does.
  }
}

/**
 * Keeps the width, or forgets it when it is the default, so a pane put back
 * with Home follows whatever the default becomes later. Never throws.
 */
export function writePaneWidth(
  storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined,
  key: string,
  width: number,
  bounds: PaneBounds,
): void {
  try {
    const value = clampPaneWidth(width, bounds)
    if (value === bounds.initial) storage?.removeItem(key)
    else storage?.setItem(key, String(value))
  } catch {
    // Blocked storage: the width lasts as long as the page does.
  }
}
